import { ReactElement, useState } from 'react'
import dayjs from 'dayjs'
import { useForm } from '@tanstack/react-form'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import axios, { pluckData, pluckError } from 'src/utilities/http-client'
import {
  CancelIcon,
  SubmitIcon,
  UploadFileIcon,
  UploadIcon,
} from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'

import {
  fetchLicenses,
  fetchLicenseVerificationStatuses,
  fetchSelectedUserLicensesCredentials,
  selectActiveLinkedService,
} from 'src/v3/manage/manage.slice'
import { DisplayRank, License } from 'src/v3/core/types'
import LicenseTypeDropdown from 'src/v3/components/form-fields/license-type-dropdown'
import StateDropdown from 'src/v3/components/form-fields/state-dropdown'
import { FileData, handleImageCapture } from 'src/v3/utilities/filesystem'

type LicenseFormProps = {
  userID: string
  license?: License
  displayRank?: DisplayRank
  open: boolean
  setOpen: (status: boolean) => void
  onError: (error: string) => void
}

export let LicenseForm = ({
  userID,
  license,
  displayRank,
  open,
  setOpen,
  onError,
}: LicenseFormProps): ReactElement => {
  let dispatch = useAppDispatch()

  let linkedServiceID = useAppSelector(selectActiveLinkedService)

  let [image, setImage] = useState<FileData | undefined>(undefined)
  let [saving, setSaving] = useState(false)

  let form = useForm({
    defaultValues: {
      number: license?.number ?? '',
      state:
        !!license?.state && license.state !== 'Other' && license.state !== ''
          ? license?.state
          : 'unselected',
      type:
        !!license?.type && license.type !== '' ? license?.type : 'unselected',
      issueDate: license?.issueDate ?? '',
      expiration: license?.expiration ?? '',
    },
    onSubmit: async ({ value, formApi }) => {
      let request = !license ? axios.post : axios.put
      setSaving(true)
      request('/api/manage/license', {
        id: license?.id,
        userID: !license ? userID : undefined,
        number: value.number,
        state: value.state,
        type: value.type,
        issueDate: value.issueDate || undefined,
        expiration: value.expiration,
        displayRank,
        linkedServiceID,
      })
        .then(pluckData)
        .then((data) => {
          if (image) {
            axios
              .put<string>('/api/manage/license/image', {
                id: license?.id || data.id,
                imageType:
                  image.extension === 'jpeg' || image.extension === 'jpg'
                    ? 'jpg'
                    : image.extension === 'png'
                      ? 'png'
                      : 'pdf',
              })
              .then(pluckData)
              .then((response) =>
                axios.put(response, image?.file, {
                  headers: {
                    'Cache-Control': 'max-age=2592000',
                    'content-type': image?.type,
                  },
                }),
              )
          }
        })
        .then(() => dispatch(fetchLicenses(linkedServiceID)))
        .then(() => dispatch(fetchLicenseVerificationStatuses(linkedServiceID)))
        .then(() => dispatch(fetchSelectedUserLicensesCredentials(userID)))
        .then(() => setOpen(false))
        .then(() => formApi.reset())
        .catch((err) => {
          onError?.(pluckError(err, 'Error Updating License'))
        })
        .finally(() => setSaving(false))
    },
  })

  let handleOnClose = () => {
    form.reset()
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleOnClose}>
      <Paper sx={{ width: 300, px: 2, py: 1 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          {!license ? 'Add' : 'Edit'} License
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <form.Field
              name="type"
              validators={{
                onChange: ({ value }) =>
                  value === 'unselected'
                    ? 'License Type is a required field'
                    : undefined,
              }}
              children={(field) => (
                <LicenseTypeDropdown
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors.length > 0}
                  errorText={field.state.meta.errors.join(', ')}
                  variant="outlined"
                  bgDefaultColor="background.paper"
                />
              )}
            />
            <form.Field
              name="state"
              validators={{
                onChange: ({ value }) =>
                  value === 'unselected'
                    ? 'License State is a required field'
                    : undefined,
              }}
              children={(field) => (
                <StateDropdown
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors.length > 0}
                  errorText={field.state.meta.errors.join(', ')}
                  variant="outlined"
                  bgDefaultColor="background.paper"
                />
              )}
            />
            <form.Field
              name="number"
              validators={{
                onChange: ({ value }) =>
                  !value ? 'License Number is a required field.' : undefined,
              }}
              children={(field) => (
                <TextField
                  fullWidth
                  variant="outlined"
                  sx={{ bgcolor: 'background.paper' }}
                  value={field.state.value}
                  label="Number"
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors.length > 0}
                  helperText={field.state.meta.errors.join(', ')}
                />
              )}
            />
            <form.Field
              name="issueDate"
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Issue Date"
                    name={field.name}
                    value={dayjs(field.state.value)}
                    onChange={(value) =>
                      field.handleChange(
                        !value ? '' : value.format('MM/DD/YYYY'),
                      )
                    }
                    slotProps={{
                      textField: {
                        sx: { bgcolor: 'background.paper' },
                        error: field.state.meta.errors.length > 0,
                        helperText: field.state.meta.errors.join(', '),
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            <form.Field
              name="expiration"
              validators={{
                onChange: ({ value }) =>
                  !value ? 'License Expiration is a required field' : undefined,
              }}
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Expiration"
                    name={field.name}
                    value={dayjs(field.state.value)}
                    onChange={(value) =>
                      field.handleChange(
                        !value ? '' : value.format('MM/DD/YYYY'),
                      )
                    }
                    slotProps={{
                      textField: {
                        sx: { bgcolor: 'background.paper' },
                        error: field.state.meta.errors.length > 0,
                        helperText: field.state.meta.errors.join(', '),
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            <Box>
              <label style={{ marginTop: 0 }}>
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                >
                  <input
                    accept="image/*,.pdf"
                    style={{ display: 'none' }}
                    type="file"
                    onChange={(e) => handleImageCapture(e, setImage, onError)}
                  />
                  {!license || !license?.imageName ? 'Select' : 'Overwrite'}{' '}
                  File
                </Button>
              </label>
              {image && (
                <Typography variant="subtitle1">
                  <UploadFileIcon
                    color="primary"
                    sx={{ mr: '2px', mb: '2px' }}
                  />
                  {image.name}
                </Typography>
              )}
              {license?.imageName && (
                <Typography color="text.secondary" variant="subtitle2">
                  *The existing license file will only be overwritten upon
                  submitting this form with a new image uploaded.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: 1.5,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              onClick={handleOnClose}
              color="error"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Paper>
    </Dialog>
  )
}
